import React, { useState, useEffect } from 'react';
import { Users, Edit, Trash2, Shield, Search, Plus, Crown, User as UserIcon, Timer, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const UsersManagement = ({ token, i18n }) => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [formData, setFormData] = useState({
    role: 'user',
    subscription_status: 'trial',
    is_banned: false,
    // Days to add to each license's subscription end date. Independent so
    // an admin can extend B and Taxi separately in one dialog.
    b_days_to_add: 0,
    // Taxi subscription controls — parallel to B-körkort. Default status is
    // computed at open time from the user's current taxi_subscription_status.
    taxi_subscription_status: 'trial',
    taxi_days_to_add: 0,
  });

  // Update time every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch a large page so the admin panel always shows every registered
      // user without needing to paginate. The endpoint defaults to limit=50
      // which was hiding recently registered users from the admin view.
      const res = await axios.get(`${API}/admin/users?limit=10000`, { headers });
      setUsers(res.data.users || []);
      if (typeof res.data.total === 'number') {
        setTotalUsers(res.data.total);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في جلب المستخدمين' : 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      role: user.role || 'user',
      subscription_status: user.subscription_status || 'trial',
      is_banned: user.is_banned || false,
      b_plan_choice: 'none',
      taxi_subscription_status: user.taxi_subscription_status || 'trial',
      taxi_plan_choice: 'none',
    });
    setModalOpen(true);
  };

  // Convert plan choice → { days, planName }.
  // 'none' → don't touch dates. 'lifetime' → 0 days but set the plan
  // flag so `_has_active_license_subscription` bypasses the end-date check.
  const _planToDays = (choice) => {
    switch (choice) {
      case 'month': return { days: 30, plan: 'monthly' };
      case 'six_months': return { days: 180, plan: '6_månader' };
      case 'year': return { days: 365, plan: 'yearly' };
      case 'lifetime': return { days: 0, plan: 'lifetime' };
      default: return { days: 0, plan: null };
    }
  };

  const handleUpdate = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // 1) Update the general user fields (role, ban, B-körkort status).
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        role: formData.role,
        subscription_status: formData.subscription_status,
        is_banned: formData.is_banned,
      }, { headers });

      // 2) B-körkort plan choice → subscription endpoint.
      const bPlan = _planToDays(formData.b_plan_choice);
      if (formData.b_plan_choice !== 'none') {
        await axios.put(`${API}/admin/users/${editingUser.id}/subscription`, {
          user_id: editingUser.id,
          subscription_status: bPlan.plan === 'lifetime' ? 'active' : (formData.subscription_status || 'active'),
          subscription_plan: bPlan.plan,
          days_to_add: bPlan.days,
          licenseType: 'b_korkort',
        }, { headers });
      }

      // 3) Taxi plan choice — or plain status change.
      const taxiPlan = _planToDays(formData.taxi_plan_choice);
      const statusChanged = (editingUser.taxi_subscription_status || 'trial') !== formData.taxi_subscription_status;
      if (formData.taxi_plan_choice !== 'none' || statusChanged) {
        await axios.put(`${API}/admin/users/${editingUser.id}/subscription`, {
          user_id: editingUser.id,
          subscription_status: taxiPlan.plan === 'lifetime' ? 'active' : formData.taxi_subscription_status,
          subscription_plan: taxiPlan.plan,
          days_to_add: taxiPlan.days,
          licenseType: 'taxi',
        }, { headers });
      }

      toast.success(i18n.language === 'ar' ? 'تم تحديث المستخدم' : 'User updated successfully');
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في التحديث' : 'Error updating user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذا المستخدم؟' : 'Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم حذف المستخدم' : 'User deleted');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting user');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { color: 'bg-gradient-to-r from-purple-500 to-pink-500', icon: Crown, text: 'Super Admin' },
      admin: { color: 'bg-gradient-to-r from-blue-500 to-cyan-500', icon: Shield, text: 'Admin' },
      editor: { color: 'bg-gradient-to-r from-emerald-500 to-teal-500', icon: Edit, text: 'Editor' },
      moderator: { color: 'bg-gradient-to-r from-amber-500 to-orange-500', icon: Shield, text: 'Moderator' },
      user: { color: 'bg-slate-500', icon: UserIcon, text: 'User' }
    };
    
    const badge = badges[role] || badges.user;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-xs font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-700',
      trial: 'bg-amber-100 text-amber-700',
      expired: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || colors.expired}`}>
        {status}
      </span>
    );
  };

  // Calculate remaining time for demo accounts
  const getRemainingTime = (expiresAt) => {
    if (!expiresAt) return null;
    
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) {
      return { expired: true, text: i18n.language === 'ar' ? 'منتهي' : 'Expired' };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${days}ي ${hours % 24}س` : `${days}d ${hours % 24}h`,
        urgent: false
      };
    } else if (hours > 0) {
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${hours}س ${minutes}د` : `${hours}h ${minutes}m`,
        urgent: hours < 2
      };
    } else {
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${minutes}د` : `${minutes}m`,
        urgent: true
      };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-emerald-600" />
              {i18n.language === 'ar' ? 'إدارة المستخدمين' : 'User Management'}
            </h3>
            <p className="text-slate-500 mt-1">
              {i18n.language === 'ar' ? `إجمالي المستخدمين: ${totalUsers || users.length}` : `Total Users: ${totalUsers || users.length}`}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder={i18n.language === 'ar' ? 'بحث عن مستخدم...' : 'Search users...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'المستخدم' : 'User'}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'الدور' : 'Role'}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'B-körkort' : 'B-körkort'}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'Taxikort' : 'Taxikort'}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'الموقت' : 'Timer'}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'آخر ظهور' : 'Last seen'}
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">
                  {i18n.language === 'ar' ? 'إجراءات' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredUsers.map((user) => {
                const remaining = user.is_demo_account ? getRemainingTime(user.expires_at) : null;
                return (
                <tr key={user.id} className={`hover:bg-slate-50 ${remaining?.expired ? 'bg-red-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          {user.name}
                          {user.is_demo_account && (
                            <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full">Demo</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(user.subscription_status)}
                      {user.subscription_end_date && (
                        <span className="text-[10px] text-slate-500">
                          {new Date(user.subscription_end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(user.taxi_subscription_status || 'trial')}
                      {user.taxi_subscription_end_date && (
                        <span className="text-[10px] text-slate-500">
                          {new Date(user.taxi_subscription_end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_demo_account ? (
                      remaining ? (
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          remaining.expired 
                            ? 'bg-red-100 text-red-700' 
                            : remaining.urgent 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          <Timer className="w-3 h-3" />
                          {remaining.text}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {(user.last_active || user.last_login) ? new Date(user.last_active || user.last_login).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        className="hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                      {user.role !== 'super_admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          className="hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {i18n.language === 'ar' ? 'تعديل المستخدم' : 'Edit User'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                {i18n.language === 'ar' ? 'الدور' : 'Role'}
              </Label>
              <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🚗</span>
                <Label className="text-sm font-bold">
                  {i18n.language === 'ar' ? 'اشتراك B-körkort' : 'B-körkort Subscription'}
                </Label>
              </div>
              <Label className="text-xs text-slate-500 mb-1 block">
                {i18n.language === 'ar' ? 'حالة الاشتراك' : 'Status'}
              </Label>
              <Select value={formData.subscription_status} onValueChange={(val) => setFormData({...formData, subscription_status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 mt-3 mb-1 block">
                {i18n.language === 'ar' ? '📦 اختر الباقة (اختياري)' : '📦 Choose plan (optional)'}
              </Label>
              <Select value={formData.b_plan_choice} onValueChange={(val) => setFormData({...formData, b_plan_choice: val})}>
                <SelectTrigger data-testid="b-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{i18n.language === 'ar' ? '➖ بدون تغيير' : '➖ No change'}</SelectItem>
                  <SelectItem value="month">{i18n.language === 'ar' ? '🗓️ شهر (30 يوم)' : '🗓️ 1 month (30 days)'}</SelectItem>
                  <SelectItem value="six_months">{i18n.language === 'ar' ? '📅 6 أشهر (180 يوم)' : '📅 6 months (180 days)'}</SelectItem>
                  <SelectItem value="year">{i18n.language === 'ar' ? '📆 سنة (365 يوم)' : '📆 1 year (365 days)'}</SelectItem>
                  <SelectItem value="lifetime">{i18n.language === 'ar' ? '👑 مدى الحياة' : '👑 Lifetime'}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                {i18n.language === 'ar' ? 'تُضاف المدة فوق تاريخ الانتهاء الحالي' : 'Adds duration on top of current expiry'}
              </p>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🚕</span>
                <Label className="text-sm font-bold">
                  {i18n.language === 'ar' ? 'اشتراك Taxikort' : 'Taxikort Subscription'}
                </Label>
              </div>
              <Label className="text-xs text-slate-500 mb-1 block">
                {i18n.language === 'ar' ? 'حالة الاشتراك' : 'Status'}
              </Label>
              <Select value={formData.taxi_subscription_status} onValueChange={(val) => setFormData({...formData, taxi_subscription_status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Label className="text-xs text-slate-500 mt-3 mb-1 block">
                {i18n.language === 'ar' ? '📦 اختر الباقة (اختياري)' : '📦 Choose plan (optional)'}
              </Label>
              <Select value={formData.taxi_plan_choice} onValueChange={(val) => setFormData({...formData, taxi_plan_choice: val})}>
                <SelectTrigger data-testid="taxi-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{i18n.language === 'ar' ? '➖ بدون تغيير' : '➖ No change'}</SelectItem>
                  <SelectItem value="month">{i18n.language === 'ar' ? '🗓️ شهر (30 يوم)' : '🗓️ 1 month (30 days)'}</SelectItem>
                  <SelectItem value="six_months">{i18n.language === 'ar' ? '📅 6 أشهر (180 يوم)' : '📅 6 months (180 days)'}</SelectItem>
                  <SelectItem value="year">{i18n.language === 'ar' ? '📆 سنة (365 يوم)' : '📆 1 year (365 days)'}</SelectItem>
                  <SelectItem value="lifetime">{i18n.language === 'ar' ? '👑 مدى الحياة' : '👑 Lifetime'}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                {i18n.language === 'ar' ? 'تُضاف المدة فوق تاريخ الانتهاء الحالي' : 'Adds duration on top of current expiry'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_banned"
                checked={formData.is_banned}
                onChange={(e) => setFormData({...formData, is_banned: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="is_banned" className="text-sm font-medium cursor-pointer">
                {i18n.language === 'ar' ? 'محظور' : 'Banned'}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700">
              {i18n.language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
